import { useEffect, useRef, useState } from "react";
import { ApiResult } from "./types.js";

/**
 * Minimal request-once hook. Re-runs when `deps` change. Returns
 * `{ data, error, status }` and discards in-flight responses if a dep
 * change supersedes them.
 *
 * Kept intentionally tiny — Mesterplan today uses Firestore's onSnapshot
 * for live updates; for one-shot fetches the existing app doesn't need a
 * react-query / swr-level cache yet. If a future cluster needs it, swap
 * the body of this hook without changing the public shape.
 */
export type QueryStatus = "idle" | "loading" | "success" | "error";

export interface QueryResult<T> {
  status: QueryStatus;
  data: T | null;
  error: { status: number; detail: string } | null;
  /** Force a refetch (e.g. on pull-to-refresh). */
  refetch: () => void;
}

export function useApiQuery<T>(
  fetcher: () => Promise<ApiResult<T>>,
  deps: ReadonlyArray<unknown>,
): QueryResult<T> {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<{ status: number; detail: string } | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  // Stash the fetcher in a ref so it doesn't trigger the effect on every render —
  // only `deps` and an explicit refetch should retrigger.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Distinguish a deps change (a genuinely new resource → surface "loading")
  // from a background refetch (same deps, refetch() bumped the key → keep the
  // current data so the screen doesn't blank). Without this, refetch() resets
  // status to "loading", which trips the app's full-page loading gate and
  // remounts the active route.
  const prevDepsRef = useRef<ReadonlyArray<unknown> | null>(null);
  const hasDataRef = useRef(false);
  hasDataRef.current = data !== null;

  useEffect(() => {
    let cancelled = false;
    const prev = prevDepsRef.current;
    const depsChanged =
      prev === null ||
      prev.length !== deps.length ||
      prev.some((d, i) => !Object.is(d, deps[i]));
    prevDepsRef.current = deps;
    // Only blank to "loading" on a new resource or the first load; a background
    // refetch that already has data keeps its data + "success".
    if (depsChanged || !hasDataRef.current) {
      setStatus("loading");
      setError(null);
    }
    void fetcherRef
      .current()
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setData((res.data ?? null) as T | null);
          setStatus("success");
        } else {
          setError({
            status: res.status,
            detail: res.error?.detail ?? res.error?.title ?? `HTTP ${res.status}`,
          });
          setStatus("error");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError({
          status: 0,
          detail: err instanceof Error ? err.message : String(err),
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refetchKey]);

  return {
    status,
    data,
    error,
    refetch: () => setRefetchKey((k) => k + 1),
  };
}
